import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Pricing from "./pages/Pricing";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Leads from "./pages/Leads";
import Contacts from "./pages/Contacts";
import Accounts from "./pages/Accounts";
import Deals from "./pages/Deals";
import Outreach from "./pages/Outreach";
import Calls from "./pages/Calls";
import Proposals from "./pages/Proposals";
import Analytics from "./pages/Analytics";
import Settings from "./pages/Settings";
import Notifications from "./pages/Notifications";
import Products from "./pages/Products";
import Contracts from "./pages/Contracts";
import ContractViewer from "./pages/ContractViewer";
import PublicContractSign from "./pages/PublicContractSign";
import Objections from "./pages/Objections";
import CustomerOnboarding from "./pages/CustomerOnboarding";
import AIInsights from "./pages/AIInsights";
import NotFound from "./pages/NotFound";
import Install from "./pages/Install";

// Product Pages
import AISalesOS from "./pages/products/AISalesOS";
import AIRevenueIntelligence from "./pages/products/AIRevenueIntelligence";
import AILeadGeneration from "./pages/products/AILeadGeneration";
import AISalesCoaching from "./pages/products/AISalesCoaching";
import GTMAutomation from "./pages/products/GTMAutomation";

// Comparison Pages
import VsGong from "./pages/compare/VsGong";
import VsOutreach from "./pages/compare/VsOutreach";
import VsApollo from "./pages/compare/VsApollo";

// Legal Pages
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsOfService from "./pages/legal/TermsOfService";
import CookiePolicy from "./pages/legal/CookiePolicy";

// Other Pages
import Integrations from "./pages/Integrations";

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <InstallPrompt />
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/install" element={<Install />} />
                <Route path="/subscription-success" element={<SubscriptionSuccess />} />
                <Route path="/sign" element={<PublicContractSign />} />
                
                {/* Product Pages */}
                <Route path="/products/ai-sales-os" element={<AISalesOS />} />
                <Route path="/products/revenue-intelligence" element={<AIRevenueIntelligence />} />
                <Route path="/products/lead-generation" element={<AILeadGeneration />} />
                <Route path="/products/sales-coaching" element={<AISalesCoaching />} />
                <Route path="/products/gtm-automation" element={<GTMAutomation />} />
                
                {/* Comparison Pages */}
                <Route path="/compare/vs-gong" element={<VsGong />} />
                <Route path="/compare/vs-outreach" element={<VsOutreach />} />
                <Route path="/compare/vs-apollo" element={<VsApollo />} />
                
                {/* Legal Pages */}
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/cookies" element={<CookiePolicy />} />
                
                {/* Integrations */}
                <Route path="/integrations" element={<Integrations />} />
                
                <Route
                  path="/onboarding" 
                  element={
                    <ProtectedRoute skipOnboardingCheck>
                      <Onboarding />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/app"
                  element={
                    <ProtectedRoute>
                      <AppLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Dashboard />} />
                  <Route path="leads" element={<Leads />} />
                  <Route path="contacts" element={<Contacts />} />
                  <Route path="accounts" element={<Accounts />} />
                  <Route path="deals" element={<Deals />} />
                  <Route path="outreach" element={<Outreach />} />
                  <Route path="calls" element={<Calls />} />
                  <Route path="proposals" element={<Proposals />} />
                  <Route path="contracts" element={<Contracts />} />
                  <Route path="contracts/:id" element={<ContractViewer />} />
                  <Route path="analytics" element={<Analytics />} />
                  <Route path="products" element={<Products />} />
                  <Route path="objections" element={<Objections />} />
                  <Route path="customer-onboarding" element={<CustomerOnboarding />} />
                  <Route path="ai-insights" element={<AIInsights />} />
                  <Route path="notifications" element={<Notifications />} />
                  <Route path="settings" element={<Settings />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
